import { Building2Icon, CameraIcon, ChevronRightIcon } from "lucide-react";
import { useRef, useState } from "react";

import { updateOrganizationLogo, type OrgView } from "../../state/orgs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { encodeAvatarFile } from "./AccountSettings.logic";
import { SettingsCard, SettingsCardRow as SettingsRow, SettingsPageHeader } from "./settingsCard";
import { SettingsPageContainer } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { FieldSaveBar, useFieldSave, useOrgSettingsPage } from "./orgSettingsShared";

interface BrandingDraft {
  readonly primaryColor: string;
  readonly secondaryColor: string;
  readonly accentColor: string;
  readonly toneOfVoice: string;
  readonly brandGuidelines: string;
}

const sameDraft = (a: BrandingDraft, b: BrandingDraft): boolean =>
  a.primaryColor === b.primaryColor &&
  a.secondaryColor === b.secondaryColor &&
  a.accentColor === b.accentColor &&
  a.toneOfVoice === b.toneOfVoice &&
  a.brandGuidelines === b.brandGuidelines;

/**
 * One uploadable mark: the current image (or placeholder) is the button —
 * click to pick a file, hover shows the camera overlay — plus Remove.
 */
function LogoUploadControl({
  org,
  mode,
  disabled,
}: {
  org: OrgView;
  mode: "light" | "dark";
  disabled: boolean;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const current = mode === "dark" ? (org.logoDarkUrl ?? null) : org.logoUrl;

  const apply = async (logo: string | null) => {
    setIsSaving(true);
    setError(null);
    const failure = await updateOrganizationLogo(org.id, mode, logo);
    setError(failure);
    setIsSaving(false);
  };

  const handleFilePicked = async (file: File | undefined) => {
    if (!file) return;
    setIsSaving(true);
    setError(null);
    try {
      const encoded = await encodeAvatarFile(file);
      const failure = await updateOrganizationLogo(org.id, mode, encoded);
      setError(failure);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "That image could not be read.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col items-start gap-2 sm:items-end">
      <div className="flex items-center gap-3">
        <input
          ref={fileInputRef}
          accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
          className="sr-only"
          type="file"
          onChange={(event) => {
            const file = event.currentTarget.files?.[0];
            // Allow re-picking the same file after an error.
            event.currentTarget.value = "";
            void handleFilePicked(file);
          }}
        />
        <button
          type="button"
          aria-label={mode === "dark" ? "Change dark mode logo" : "Change organization logo"}
          disabled={disabled || isSaving}
          className={`group relative size-14 shrink-0 cursor-pointer overflow-hidden rounded-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-default ${
            mode === "dark" ? "bg-zinc-900" : "bg-muted"
          }`}
          onClick={() => fileInputRef.current?.click()}
        >
          {current ? (
            <img alt="" className="size-full object-cover" src={current} />
          ) : (
            <span
              className={`flex size-full items-center justify-center ${
                mode === "dark" ? "text-zinc-400" : "text-muted-foreground"
              }`}
            >
              <Building2Icon className="size-5" />
            </span>
          )}
          {disabled ? null : (
            <span
              aria-hidden="true"
              className={`absolute inset-0 flex items-center justify-center rounded-lg bg-black/45 transition-opacity ${
                isSaving
                  ? "opacity-100"
                  : "opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100"
              }`}
            >
              <CameraIcon className="size-5 text-white" />
            </span>
          )}
        </button>
        {current && !disabled ? (
          <Button size="sm" variant="ghost" disabled={isSaving} onClick={() => void apply(null)}>
            Remove
          </Button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The dark-mode mark hides behind a disclosure — most orgs never set one. */
function DarkLogoDisclosure({ org, disabled }: { org: OrgView; disabled: boolean }) {
  const [open, setOpen] = useState(Boolean(org.logoDarkUrl));

  return (
    <div className="pb-2">
      <button
        type="button"
        aria-expanded={open}
        className="flex cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronRightIcon className={`size-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
        Dark mode logo
      </button>
      {open ? (
        <div className="mt-2 flex flex-col gap-3 sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(10rem,auto)] sm:items-center sm:gap-8">
          <p className="max-w-xl text-[13px] leading-[1.45] text-muted-foreground/80">
            Shown instead of the regular logo when the workspace uses a dark theme. Falls back to
            the regular logo when unset.
          </p>
          <div className="flex w-full shrink-0 items-center gap-2 sm:w-auto sm:justify-end">
            <LogoUploadControl org={org} mode="dark" disabled={disabled} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

/** Swatch + hex input, sized like the other settings controls. */
function ColorControl({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const isValid = value.length === 0 || /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="size-7 shrink-0 rounded-md border border-border/60 bg-[repeating-conic-gradient(var(--color-muted)_0%_25%,transparent_0%_50%)] bg-[length:8px_8px]"
        style={isValid && value.length > 0 ? { backgroundColor: value, backgroundImage: "none" } : undefined}
      />
      <Input
        aria-label={label}
        value={value}
        disabled={disabled}
        placeholder="#1d4ed8"
        className="h-8 w-28 font-mono"
        onChange={(event) => onChange(event.currentTarget.value)}
      />
    </div>
  );
}

/**
 * `/$org/settings/branding` — the organization's marks and brand styling.
 * Colours, tone and guidelines feed the document pipeline (they live in
 * `Organization.designContext`), so they read the same in generated
 * documents as they do here.
 */
export function BrandingSettingsPanel() {
  const { org, profile, canManage, save, fallback } = useOrgSettingsPage("Branding");
  const branding = profile?.branding;
  const field = useFieldSave<BrandingDraft>(
    {
      primaryColor: branding?.primaryColor ?? "",
      secondaryColor: branding?.secondaryColor ?? "",
      accentColor: branding?.accentColor ?? "",
      toneOfVoice: branding?.toneOfVoice ?? "",
      brandGuidelines: branding?.brandGuidelines ?? "",
    },
    (draft) =>
      save({
        branding: {
          primaryColor: draft.primaryColor.trim() || null,
          secondaryColor: draft.secondaryColor.trim() || null,
          accentColor: draft.accentColor.trim() || null,
          toneOfVoice: draft.toneOfVoice.trim() || null,
          brandGuidelines: draft.brandGuidelines.trim() || null,
        },
      }),
    sameDraft,
  );

  if (fallback) return fallback;
  const readOnly = !canManage;

  return (
    <SettingsPageContainer className="gap-10">
      <SettingsPageHeader
        title="Branding"
        description="The organization's marks, and the style eve follows in the documents and presentations it writes."
      />
      <SettingsCard id="branding" title="Logo">
        <SettingsRow
          {...searchableSetting("organization-logo")}
          description={
            readOnly
              ? "Only organization admins can change the logo."
              : "Click the logo to upload a new one. Shown in the organization switcher and across the workspace."
          }
          control={<LogoUploadControl org={org!} mode="light" disabled={readOnly} />}
        >
          <DarkLogoDisclosure org={org!} disabled={readOnly} />
        </SettingsRow>
      </SettingsCard>

      <SettingsCard
        id="brand-style"
        title="Document style"
        description={readOnly ? "Only organization admins can change the brand style." : undefined}
        footer={<FieldSaveBar state={field} label="branding" />}
      >

        <SettingsRow
          {...searchableSetting("brand-colors")}
          description="The primary colour of generated documents and presentations. Hex values like #1d4ed8."
          control={
            <ColorControl
              label="Primary colour"
              value={field.draft.primaryColor}
              disabled={readOnly || field.isSaving}
              onChange={(primaryColor) => field.setDraft({ ...field.draft, primaryColor })}
            />
          }
        />

        <SettingsRow
          id="brand-secondary-color"
          title="Secondary colour"
          description="Supporting colour for headings, dividers and secondary emphasis."
          control={
            <ColorControl
              label="Secondary colour"
              value={field.draft.secondaryColor}
              disabled={readOnly || field.isSaving}
              onChange={(secondaryColor) => field.setDraft({ ...field.draft, secondaryColor })}
            />
          }
        />

        <SettingsRow
          id="brand-accent-color"
          title="Accent colour"
          description="Used sparingly — callouts, highlights and chart accents."
          control={
            <ColorControl
              label="Accent colour"
              value={field.draft.accentColor}
              disabled={readOnly || field.isSaving}
              onChange={(accentColor) => field.setDraft({ ...field.draft, accentColor })}
            />
          }
        />

        <SettingsRow
          {...searchableSetting("tone-of-voice")}
          description="How eve should sound in documents written for this organization."
          control={
            <Input
              value={field.draft.toneOfVoice}
              disabled={readOnly || field.isSaving}
              aria-label="Tone of voice"
              placeholder="Professional, plain-spoken"
              className="h-8 w-full sm:w-56"
              onChange={(event) =>
                field.setDraft({ ...field.draft, toneOfVoice: event.currentTarget.value })
              }
            />
          }
        />

        <SettingsRow
          {...searchableSetting("brand-guidelines")}
          description="Anything else that governs how this organization's documents look and read."
        >
          <div className="space-y-2 pb-3">
            <Textarea
              value={field.draft.brandGuidelines}
              disabled={readOnly || field.isSaving}
              aria-label="Brand guidelines"
              placeholder="Cover pages carry the wordmark; body copy stays in the primary colour."
              rows={4}
              onChange={(event) =>
                field.setDraft({ ...field.draft, brandGuidelines: event.currentTarget.value })
              }
            />
          </div>
        </SettingsRow>

        {/* One save for the whole card (the footer): colours, tone and
            guidelines are one `designContext` write, so a per-row bar would
            be a lie. */}
      </SettingsCard>
    </SettingsPageContainer>
  );
}
